// src/services/stats.js (or wherever this file lives)
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Base URL
const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/stats/`;

// Create the stats API slice
export const statsApi = createApi({
  reducerPath: "statsApi",
  tagTypes: ["Stats"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  keepUnusedDataFor: 60,
  refetchOnReconnect: true,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    // Existing
    // --- Diagnostics & Monitoring ---
    // 1. Recent backend/API error logs
    getErrorLogs: builder.query({
      // GET /api/stats/errorLogs
      query: () => "errorLogs",
      providesTags: [{ type: "Stats", id: "ErrorLogs" }],
    }),

    // 2. Server health/resource usage
    getServerHealth: builder.query({
      // GET /api/stats/serverHealth
      query: () => "serverHealth",
      providesTags: [{ type: "Stats", id: "ServerHealth" }],
    }),

    // 3. Job/queue backlog
    getJobQueue: builder.query({
      // GET /api/stats/jobQueue
      query: () => "jobQueue",
      providesTags: [{ type: "Stats", id: "JobQueue" }],
    }),

    // 4. Slow queries/performance
    getSlowQueries: builder.query({
      // GET /api/stats/slowQueries
      query: () => "slowQueries",
      providesTags: [{ type: "Stats", id: "SlowQueries" }],
    }),

    // 5. Recent login/auth failures
    getLoginFailures: builder.query({
      // GET /api/stats/loginFailures
      query: () => "loginFailures",
      providesTags: [{ type: "Stats", id: "LoginFailures" }],
    }),

    // 6. Service/external dependency status
    getServiceStatus: builder.query({
      // GET /api/stats/serviceStatus
      query: () => "serviceStatus",
      providesTags: [{ type: "Stats", id: "ServiceStatus" }],
    }),

    // 7. Version/deployment info
    getVersionInfo: builder.query({
      // GET /api/stats/versionInfo
      query: () => "versionInfo",
      providesTags: [{ type: "Stats", id: "VersionInfo" }],
    }),

    // 8. Feature flags/config
    getFeatureFlags: builder.query({
      // GET /api/stats/featureFlags
      query: () => "featureFlags",
      providesTags: [{ type: "Stats", id: "FeatureFlags" }],
    }),
    getSubmissionStats: builder.query({
      query: () => "submission-stats",
      providesTags: [{ type: "Stats", id: "SubmissionStats" }],
    }),
    getOnlineGeneralApps: builder.query({
      query: () => "onlineGeneralApps",
      providesTags: [{ type: "Stats", id: "OnlineGeneralApps" }],
    }),
    getOnlineDasApps: builder.query({
      query: () => "onlineDasApps",
      providesTags: [{ type: "Stats", id: "OnlineDasApps" }],
    }),
    getApplicationCount: builder.query({
      query: () => "applicationCount",
      providesTags: [{ type: "Stats", id: "ApplicationCount" }],
    }),
    getDasApplicationCount: builder.query({
      query: () => "dasApplicationCount",
      providesTags: [{ type: "Stats", id: "DasApplicationCount" }],
    }),
    getSessionList: builder.query({
      query: () => "sessionList",
      providesTags: [{ type: "Stats", id: "SessionList" }],
    }),
    getSessionSummary: builder.query({
      query: () => "sessionSummary",
      providesTags: [{ type: "Stats", id: "SessionSummary" }],
    }),
    getBlockingSessions: builder.query({
      query: () => "blockingSessions",
      providesTags: [{ type: "Stats", id: "BlockingSessions" }],
    }),

    // ✅ NEW monitors
    // Expecting: { requests: [...] } OR just [...]
    getActiveRequests: builder.query({
      query: () => "activeRequests",
      providesTags: [{ type: "Stats", id: "ActiveRequests" }],
    }),

    // Expecting: { transactions: [...] } OR just [...]
    getOpenTransactions: builder.query({
      query: () => "openTransactions",
      providesTags: [{ type: "Stats", id: "OpenTransactions" }],
    }),

    // ✅ Kill actions (wire these to backend routes)
    // POST /api/stats/killSession  { sessionId }
    killSession: builder.mutation({
      query: ({ sessionId }) => ({
        url: "killSession",
        method: "POST",
        body: { sessionId },
      }),
      invalidatesTags: [
        { type: "Stats", id: "BlockingSessions" },
        { type: "Stats", id: "ActiveRequests" },
        { type: "Stats", id: "OpenTransactions" },
        { type: "Stats", id: "SessionList" },
        { type: "Stats", id: "SessionSummary" },
      ],
    }),

    // POST /api/stats/killRequest  { sessionId, requestId }
    killRequest: builder.mutation({
      query: ({ sessionId, requestId }) => ({
        url: "killRequest",
        method: "POST",
        body: { sessionId, requestId },
      }),
      invalidatesTags: [
        { type: "Stats", id: "BlockingSessions" },
        { type: "Stats", id: "ActiveRequests" },
        { type: "Stats", id: "OpenTransactions" },
        { type: "Stats", id: "SessionList" },
        { type: "Stats", id: "SessionSummary" },
      ],
    }),

    // Optional: single “refresh everything” button
    refreshAllStats: builder.mutation({
      query: () => ({ url: "ping", method: "GET" }), // harmless endpoint (add server-side or point to any cheap GET)
      invalidatesTags: [{ type: "Stats" }],
    }),
  }),
});

// Hooks
export const {
  useGetSubmissionStatsQuery,
  useGetOnlineGeneralAppsQuery,
  useGetOnlineDasAppsQuery,
  useGetApplicationCountQuery,
  useGetDasApplicationCountQuery,
  useGetSessionListQuery,
  useGetSessionSummaryQuery,
  useGetBlockingSessionsQuery,

  // ✅ new
  useGetActiveRequestsQuery,
  useGetOpenTransactionsQuery,
  useKillSessionMutation,
  useKillRequestMutation,
  useRefreshAllStatsMutation,

  // --- Diagnostics & Monitoring ---
  useGetErrorLogsQuery,
  useGetServerHealthQuery,
  useGetJobQueueQuery,
  useGetSlowQueriesQuery,
  useGetLoginFailuresQuery,
  useGetServiceStatusQuery,
  useGetVersionInfoQuery,
  useGetFeatureFlagsQuery,
} = statsApi;

export default statsApi;
